import { Clock } from 'lucide-react'
import { fmtDateTime, toDate } from '../utils/format'

interface BoxTruckAvailableAtNoteProps {
    /** ISO instant the driver named, as the server resolved it. */
    availableAt?: string | null
    /** Their own words for it ("tomorrow 8am"). */
    availableAtText?: string | null
}

/**
 * "Available tomorrow 8am" — what the DRIVER said about a position that is not yet true.
 *
 * It sits beside the location age rather than in the Availability column on purpose: that column is the
 * operator's own status (Available / Unavailable / Booked), set by the desk, and this is the driver's word
 * about the pin next to it. A driver who texts "Tomorrow 10512" has given us a real place and a real time,
 * and the whole point of storing them together is that the row can say so.
 *
 * The driver's phrase is the label and the resolved instant is the tooltip, never the other way round: the
 * parse is a best effort over free text, so the operator should read the sentence and see our reading of it
 * only when they look closer.
 *
 * Renders nothing when there is no availability, which is the common case — a check-in usually means "I am
 * here now", and the age badge beside it already says when that was. The server drops a `availableAt` that
 * has passed, so there is no second staleness rule here.
 */
export const BoxTruckAvailableAtNote = ({ availableAt, availableAtText }: BoxTruckAvailableAtNoteProps) => {
    const at = toDate(availableAt)
    if (!at) return null
    return (
        <span
            className="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] font-medium leading-tight text-foreground-muted"
            title={`The driver said they would be at this location ${availableAtText ? `"${availableAtText}"` : ''} — ${fmtDateTime(at)}`}
        >
            <Clock className="h-2.5 w-2.5 shrink-0" aria-hidden />
            {availableAtText?.trim() || fmtDateTime(at)}
        </span>
    )
}
